import React, { useState } from 'react';
import { Box, Button, Checkbox, Heading, HStack, Input, Select, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr } from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import MainLayout from '../../shared/components/MainLayout';
import { AccessGate, useAllowedMutation, useCan } from '../../shared/access/AccessControls';
import { useAppSelector } from '../../store/hooks';
import { platformRolesApi as api } from '../../store/services/PlatformRolesService';
import { platformRoleError } from '../../shared/utils/platformRoleError';
import { isValidUuid } from '../../shared/utils/utils';

const Pages = ({ next, first, disabled }: { next?: () => void; first: () => void; disabled: boolean }) =>
  <HStack my={3}><Button onClick={first} isDisabled={disabled}>First page</Button>
    <Button onClick={next} isDisabled={disabled || !next}>Next page</Button></HStack>;

const Catalog = () => {
  const { currentData, isFetching, isError, refetch } = api.useGetPlatformRoleCatalogQuery();
  return <Box as="section" mb={6}>
    <Heading size="md">Role catalog</Heading>
    <Text>Policies are defined by the server. The actions below are platform operations; individual records may have additional restrictions.</Text>
    <Button onClick={() => refetch()} isDisabled={isFetching}>Reload catalog</Button>
    {isError && <Text role="alert">Could not load the role catalog.</Text>}
    {currentData?.roles.map(entry => <details key={entry.role}>
      <summary>{entry.role} ({entry.availableActions.length} actions)</summary>
      <ul>{entry.availableActions.map(a => <li key={a.code}>{a.name} ({a.code})</li>)}</ul>
    </details>)}
  </Box>;
};

const Roster = ({ select }: { select: (id: string) => void }) => {
  const [cursor, setCursor] = useState<string>();
  const [role, setRole] = useState('');
  const canCatalog = useCan('ADMIN_PLATFORM_ROLE_CATALOG_READ');
  const catalog = api.useGetPlatformRoleCatalogQuery(undefined, { skip: !canCatalog });
  const { currentData, isFetching, isError, refetch } = api.useGetPlatformRoleRosterQuery({ limit: 50, cursor, role: role || undefined });
  return <Box as="section" mb={6}>
    <Heading size="md">Staff role holders</Heading>
    <Text>Eligible administrators count toward the last-administrator safeguard. This flag is not a sign-in permission.</Text>
    <Select aria-label="Filter holders by role" value={role} onChange={e => { setRole(e.target.value); setCursor(undefined); }}>
      <option value="">All staff holders</option>
      {catalog.currentData?.roles.map(r => <option key={r.role} value={r.role}>{r.role}</option>)}
    </Select>
    <Button onClick={() => refetch()} isDisabled={isFetching}>Reload holders</Button>
    {isFetching && <Text role="status">Loading holders...</Text>}
    {isError && <Text role="alert">Could not load role holders.</Text>}
    {!isError && <Table><Thead><Tr><Th>User</Th><Th>Roles</Th><Th>Eligible administrator</Th><Th /></Tr></Thead><Tbody>
      {currentData?.users.map(user => <Tr key={user.userId}><Td>{user.email}<Text fontSize="xs">{user.userId}</Text></Td>
        <Td>{user.roles.join(', ')}</Td><Td>{user.isEligibleAdmin ? 'Yes' : 'No'}</Td>
        <Td><Button onClick={() => select(user.userId)}>View assignments</Button></Td></Tr>)}
    </Tbody></Table>}
    <Pages disabled={isFetching} first={() => setCursor(undefined)} next={currentData?.nextCursor ? () => setCursor(currentData.nextCursor!) : undefined} />
  </Box>;
};

const Assignments = ({ userId }: { userId: string }) => {
  const { currentData, isFetching, isError, refetch } = api.useGetPlatformRoleAssignmentsQuery(userId, { refetchOnMountOrArgChange: true });
  const canCatalog = useCan('ADMIN_PLATFORM_ROLE_CATALOG_READ');
  const catalog = api.useGetPlatformRoleCatalogQuery(undefined, { skip: !canCatalog });
  const canGrant = useCan('ADMIN_PLATFORM_ROLE_GRANT');
  const canRevoke = useCan('ADMIN_PLATFORM_ROLE_REVOKE');
  const actorId = useAppSelector(s => s.auth.profile?.userId);
  const [role, setRole] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [message, setMessage] = useState('');
  const [grant, granting] = useAllowedMutation(api.useGrantPlatformRoleMutation, 'grantPlatformRole');
  const [revoke, revoking] = useAllowedMutation(api.useRevokePlatformRoleMutation, 'revokePlatformRole');
  const assigned = currentData?.assignments.some(a => a.role === role);
  const selfAdminRevoke = assigned && userId === actorId && role === 'ADMIN';
  const loaded = !isFetching && !isError && currentData?.userId === userId &&
    !catalog.isFetching && !catalog.isError && !!catalog.currentData?.roles.some(r => r.role === role);
  const valid = !!reason.trim() && Array.from(reason).length <= 1000;
  const busy = granting.isLoading || revoking.isLoading;
  const allowed = assigned ? canRevoke : canGrant;
  const canSave = loaded && valid && !busy && allowed && (!selfAdminRevoke || confirmed);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) return;
    setMessage('');
    try {
      await (assigned ? revoke : grant)({ userId, role, changeReason: reason }).unwrap();
      setReason(''); setConfirmed(false);
      setMessage('Assignment request completed. Current assignments and access are being reloaded.');
    } catch (error) { setMessage(platformRoleError(error)); }
  };
  return <Box as="section" mb={6}>
    <Heading size="md">Assignments for {userId}</Heading>
    <Button onClick={() => refetch()} isDisabled={isFetching}>Reload assignments</Button>
    {isFetching && <Text role="status">Loading assignments...</Text>}
    {isError && <Text role="alert">Could not load assignments. Changes are disabled.</Text>}
    {!isError && <ul>{currentData?.assignments.map(a => <li key={a.id}>{a.role}</li>)}</ul>}
    <AccessGate action={['ADMIN_PLATFORM_ROLE_GRANT', 'ADMIN_PLATFORM_ROLE_REVOKE']}>
      <form onSubmit={submit}>
        <Select aria-label="Platform role" value={role} onChange={e => { setRole(e.target.value); setConfirmed(false); setMessage(''); }}>
          <option value="">Choose a role</option>
          {catalog.currentData?.roles.map(r => <option key={r.role} value={r.role}>{r.role}</option>)}
        </Select>
        <Textarea aria-label="Reason for role change" value={reason} onChange={e => setReason(e.target.value)} placeholder="Required reason for this change" />
        <Text>{Array.from(reason).length}/1000 characters</Text>
        {selfAdminRevoke && <Checkbox isChecked={confirmed} onChange={e => setConfirmed(e.target.checked)}>
          I understand that revoking my ADMIN role removes my administrative operations.
        </Checkbox>}
        <Button type="submit" isDisabled={!canSave} isLoading={busy}>{assigned ? 'Revoke role' : 'Grant role'}</Button>
      </form>
    </AccessGate>
    {message && <Text role="status">{message}</Text>}
  </Box>;
};

const History = ({ userId }: { userId: string }) => {
  const [cursor, setCursor] = useState<string>();
  const { currentData, isFetching, isError, refetch } = api.useGetPlatformRoleEventsQuery({
    targetUserId: userId || undefined, cursor, limit: 50,
  });
  return <Box as="section">
    <Heading size="md">Assignment history</Heading>
    <Text>Committed operator changes only. Initial assignments and successful no-op requests do not create history events.</Text>
    <Button onClick={() => refetch()} isDisabled={isFetching}>Reload history</Button>
    {isFetching && <Text role="status">Loading history...</Text>}
    {isError && <Text role="alert">Could not load assignment history.</Text>}
    {!isError && <Table><Thead><Tr><Th>Time</Th><Th>Actor / target</Th><Th>Change</Th><Th>Reason / request ID</Th></Tr></Thead><Tbody>
      {currentData?.events.map(e => <Tr key={e.id}><Td>{e.createdAt}</Td><Td>{e.actorUserId}<br />{e.targetUserId}</Td>
        <Td>{e.role}: {e.beforeAssigned ? 'assigned' : 'absent'} → {e.afterAssigned ? 'assigned' : 'absent'}</Td>
        <Td>{e.changeReason}<Text fontSize="xs">{e.requestId}</Text></Td></Tr>)}
    </Tbody></Table>}
    <Pages disabled={isFetching} first={() => setCursor(undefined)} next={currentData?.nextCursor ? () => setCursor(currentData.nextCursor!) : undefined} />
  </Box>;
};

const PlatformRoles = () => {
  const [params, setParams] = useSearchParams();
  const userId = params.get('userId') || '';
  const validUserId = !!userId && isValidUuid(userId);
  const [draft, setDraft] = useState(userId);
  const select = (id: string) => { setDraft(id); setParams(id ? { userId: id } : {}); };
  return <MainLayout><Box p={4}>
    <Heading size="lg" mb={4}>Platform roles</Heading>
    <AccessGate action="ADMIN_PLATFORM_ROLE_CATALOG_READ"><Catalog /></AccessGate>
    <AccessGate action="ADMIN_PLATFORM_ROLE_ASSIGNMENTS_READ">
      <Roster select={select} />
      <form onSubmit={e => { e.preventDefault(); if (isValidUuid(draft)) select(draft); }}>
        <HStack><Input aria-label="User ID" value={draft} onChange={e => setDraft(e.target.value)} placeholder="User ID (not profile ID)" />
          <Button type="submit" isDisabled={!isValidUuid(draft)}>Load assignments</Button>
          <Button onClick={() => select('')}>Clear selection</Button></HStack>
      </form>
      {userId && !validUserId && <Text role="alert">Enter a valid user ID.</Text>}
      {validUserId && <Assignments key={userId} userId={userId} />}
    </AccessGate>
    <AccessGate action="ADMIN_PLATFORM_ROLE_HISTORY_READ"><History key={userId} userId={validUserId ? userId : ''} /></AccessGate>
  </Box></MainLayout>;
};
export default PlatformRoles;
